import { useEffect, useState } from "react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useDepositSettings } from "@/hooks/useDepositSettings";
import { ContactInfo } from "@/components/ContactInfo";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";

const TermsOfUse = () => {
  const { depositAmount } = useDepositSettings();
  const depositLabel = depositAmount > 0 ? `$${depositAmount}` : "";
  const [rewardsEnabled, setRewardsEnabled] = useState(false);
  const [rewardTiers, setRewardTiers] = useState<{ referrals_needed: number; reward_description: string }[]>([]);

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["reward_program_enabled", "reward_tiers"]);
      data?.forEach((s: any) => {
        if (s.key === "reward_program_enabled") setRewardsEnabled(s.value === "true");
        if (s.key === "reward_tiers") {
          try { setRewardTiers(JSON.parse(s.value)); } catch {}
        }
      });
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO
        path="/terms-of-use"
        title="Terms of Use — In Paradise Beauty"
        description="Review the terms and conditions for using the In Paradise Beauty website and booking services."
      />
      <Navbar />
      <section className="pt-32 pb-24">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-light tracking-[0.5em] uppercase mb-3">Legal</p>
            <h1 className="font-serif text-4xl md:text-5xl font-bold italic">Terms of Use</h1>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground font-light leading-[2] tracking-wide space-y-8">
            <p className="text-xs text-muted-foreground/70 uppercase tracking-[0.3em]">
              Last Updated: April 19, 2026
            </p>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the In Paradise Beauty website and services, you agree to be bound by these Terms of Use. If you do not agree, please discontinue use of the website and services.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">2. Services</h2>
              <p>
                In Paradise Beauty provides luxury beauty services including nails, lashes, pedicures, and dermaplaning. All services are subject to availability and must be booked through the website or by contacting us directly.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">3. Booking & Appointments</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>All bookings are subject to the cancellation, deposit, and late policies.</li>
                <li>A non-refundable {depositLabel ? `${depositLabel} ` : ""}deposit is required to secure your appointment.</li>
                <li>You must be at least 18 years old to book an appointment, or have parental/guardian consent.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">4. User Accounts &amp; Account Deletion</h2>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to provide accurate and current information when creating your account. You may delete your account at any time from within the app via <strong className="text-foreground">Account → Delete Account</strong>; deletion is permanent and removes your profile, bookings, photos, rewards, and sign-in credentials, subject to limited records we are legally required to retain.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">4a. Mobile Application License</h2>
              <p>
                Subject to your compliance with these Terms, In Paradise Beauty grants you a limited, non-exclusive, non-transferable, revocable license to install and use the mobile application on a device that you own or control, solely for your personal, non-commercial use of the services. If you obtained the app through the Apple App Store, this license is further limited as set forth in the Apple App Store Terms of Service. You agree not to copy, modify, reverse engineer, or attempt to extract the source code of the app, except to the extent applicable law expressly permits.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">4b. Apple App Store Terms (iOS)</h2>
              <p>
                If you downloaded the mobile application from the Apple App Store, you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 space-y-1 mt-2">
                <li>These Terms are between you and In Paradise Beauty only, not with Apple. Apple is not responsible for the app or its content.</li>
                <li>Your license to use the app is limited to use on Apple-branded products that you own or control, as permitted by the Apple Media Services Terms.</li>
                <li>Apple has no obligation to provide maintenance or support for the app.</li>
                <li>In the event of any failure of the app to conform to any applicable warranty, you may notify Apple, who may refund the purchase price (if any). Otherwise, Apple has no warranty obligation.</li>
                <li>In Paradise Beauty (not Apple) is responsible for addressing any claims relating to the app, including product liability, regulatory compliance, and intellectual property claims.</li>
                <li>Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you.</li>
              </ul>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">5. Intellectual Property</h2>
              <p>
                All content on this website — including text, images, logos, and design — is the property of In Paradise Beauty and is protected by copyright and intellectual property laws. You may not reproduce, distribute, or use any content without prior written consent.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">6. Limitation of Liability</h2>
              <p>
                In Paradise Beauty shall not be liable for any indirect, incidental, or consequential damages arising from your use of the website or services. Liability is limited to the amount you paid for the specific service in question.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">7. Prohibited Conduct</h2>
              <ul className="list-disc pl-6 space-y-1">
                <li>Using the website for any unlawful purpose</li>
                <li>Attempting to gain unauthorized access to the systems</li>
                <li>Submitting false or misleading information</li>
                <li>Interfering with the proper functioning of the website</li>
              </ul>
            </div>

            {rewardsEnabled && (
              <div>
                <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">8. Referral & Rewards Program</h2>
                <p>
                  In Paradise Beauty offers a referral and rewards program subject to the following terms. Participation is voluntary and may be modified or discontinued at any time.
                </p>
                <ul className="list-disc pl-6 space-y-1 mt-3">
                  <li>Each registered client receives a unique referral code that can be shared with friends and family.</li>
                  <li>When a new client signs up using your referral code and completes a booking, both the referrer and the referred client may earn rewards.</li>
                  <li>Rewards are issued as credit coupons applied to future bookings and cannot be redeemed for cash.</li>
                  <li>All coupons have an expiration date. Expired coupons cannot be honored or extended.</li>
                  <li>Coupons are non-transferable and can only be used by the account holder they were issued to.</li>
                  <li>Abuse, fraud, or manipulation of the referral system (including self-referrals or fake accounts) will result in forfeiture of all rewards and potential account suspension.</li>
                </ul>

                {rewardTiers.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-foreground mb-2">Current Reward Tiers:</p>
                    <div className="border border-border/50 rounded-lg overflow-hidden">
                      {rewardTiers.map((tier, i) => (
                        <div key={i} className={`flex items-center justify-between px-4 py-3 text-sm ${i > 0 ? "border-t border-border/30" : ""}`}>
                          <span className="text-muted-foreground">{tier.referrals_needed} referral{tier.referrals_needed !== 1 ? "s" : ""}</span>
                          <span className="text-primary font-medium">{tier.reward_description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="mt-3 text-xs text-muted-foreground/70">
                  For full details on the rewards program, see the <Link to="/rewards-policy" className="text-primary underline hover:text-primary/80 transition-colors">Rewards & Referral Policy</Link>.
                </p>
              </div>
            )}

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">{rewardsEnabled ? "9" : "8"}. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Use at any time. Changes will be effective upon posting to this page. Your continued use of the website constitutes acceptance of the updated terms.
              </p>
            </div>

            <div>
              <h2 className="font-serif text-xl font-semibold text-foreground italic mb-3">{rewardsEnabled ? "10" : "9"}. Contact</h2>
              <p>
                For questions regarding these Terms of Use, please contact us at:
              </p>
              <ContactInfo />
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default TermsOfUse;
